import { useCallback, useEffect, useRef, useState } from 'react';

import { notifyBugsnagHandledError } from '../../../../utils/bugsnagClient';

/**
 * Drives the podcast speaker voice demos from a single audio element, so "only one
 * demo plays at a time" is a property of the DOM rather than something to coordinate.
 *
 * `resolveSrc` is asked, on every render, which file the currently playing field
 * ought to be playing. Any difference from what is loaded stops playback — one rule
 * that covers a voice change, a target-language change, and the field being hidden
 * (Dialog -> monologue) while its demo runs.
 */
const useVoiceDemo = (resolveSrc: (fieldName: string) => string | null) => {
	const audioRef = useRef<HTMLAudioElement>(null);
	const [playingField, setPlayingField] = useState<string | null>(null);
	const loadedSrcRef = useRef<string | null>(null);
	const requestedFieldRef = useRef<string | null>(null);
	const playRequestIdRef = useRef(0);

	// State follows the element's own events, never the click: assigning a new `src`
	// to a playing element queues its `pause` before the `play` we ask for next, so
	// deriving from events keeps a field switch from clearing the field it switched to.
	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) {
			return undefined;
		}
		const handlePlay = () => setPlayingField(requestedFieldRef.current);
		const handleStopped = () => setPlayingField(null);
		const handleError = () => {
			setPlayingField(null);
			notifyBugsnagHandledError(
				new Error(`Voice demo failed to load: ${loadedSrcRef.current ?? 'unknown'}`)
			);
		};

		audio.addEventListener('play', handlePlay);
		audio.addEventListener('pause', handleStopped);
		audio.addEventListener('ended', handleStopped);
		audio.addEventListener('error', handleError);

		return () => {
			audio.removeEventListener('play', handlePlay);
			audio.removeEventListener('pause', handleStopped);
			audio.removeEventListener('ended', handleStopped);
			audio.removeEventListener('error', handleError);
			// Leaving the form must not leave a demo playing.
			if (!audio.paused) {
				audio.pause();
			}
		};
	}, []);

	const toggle = useCallback(
		(fieldName: string) => {
			const audio = audioRef.current;
			if (!audio) {
				return;
			}
			if (playingField === fieldName) {
				audio.pause();
				return;
			}
			const src = resolveSrc(fieldName);
			if (!src) {
				return;
			}
			if (loadedSrcRef.current !== src) {
				loadedSrcRef.current = src;
				audio.src = src;
			}
			requestedFieldRef.current = fieldName;
			// The samples run 10-14s with no progress UI, so a second click restarts rather
			// than resuming into the tail of a clip the teacher cannot see the position of.
			audio.currentTime = 0;
			// play() resolves only if playback actually starts; a rejection must not leave the
			// control showing "pause" for audio that never began. Only the latest request may
			// clear the state, though: assigning the next demo's src rejects the previous play()
			// as interrupted, and that rejection can land after the new demo already started.
			const requestId = playRequestIdRef.current + 1;
			playRequestIdRef.current = requestId;
			audio.play().catch(() => {
				if (playRequestIdRef.current === requestId) {
					setPlayingField(null);
				}
			});
		},
		[playingField, resolveSrc]
	);

	const expectedSrc = playingField ? resolveSrc(playingField) : null;
	useEffect(() => {
		// Only judge the field we last asked for. Mid-switch the browser has not yet
		// fired `play` for the new field, so `playingField` still names the old one while
		// the loaded src is already the new one -- comparing those would stop the demo
		// that just started.
		if (
			playingField &&
			playingField === requestedFieldRef.current &&
			expectedSrc !== loadedSrcRef.current
		) {
			audioRef.current?.pause();
		}
	}, [expectedSrc, playingField]);

	return { audioRef, playingField, toggle };
};

export default useVoiceDemo;
